import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Users, Search, Edit, Trash2, Eye, ChevronLeft, Mail, Phone, MapPin,
  Calendar, CreditCard, FileText, Briefcase, Shield, User as UserIcon,
  X, Save, AlertTriangle, Bell, Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAppSettings } from "@/context/SettingsContext";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

const UserManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscriptionsEnabled } = useAppSettings();
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newUserCount, setNewUserCount] = useState(0);
  const [newUsersList, setNewUsersList] = useState([]);
  const [showNewUsersAlert, setShowNewUsersAlert] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    fetchUsers();
    fetchNewUsers();
  }, [user, navigate]);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, roleFilter]);

  const fetchNewUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/new-users`, { withCredentials: true });
      setNewUserCount(res.data.count);
      setNewUsersList(res.data.users);
      if (res.data.count > 0) setShowNewUsersAlert(true);
    } catch {}
  };

  const markUsersSeen = async () => {
    try {
      await axios.post(`${API}/admin/new-users/mark-seen`, {}, { withCredentials: true });
      setNewUserCount(0);
      setShowNewUsersAlert(false);
      toast.success("Notifications cleared");
    } catch {}
  };

  const openEmailClient = (email, name) => {
    const subject = encodeURIComponent(`Zig Zag Lawncare Okinawa - Hello ${name || ""}!`);
    const body = encodeURIComponent(`Hi ${name || "there"},\n\nThank you for signing up with Zig Zag Lawncare Okinawa!\n\nWe'd love to help you with your lawn care needs. Feel free to reply to this email or visit our site to request a quote.\n\nBest regards,\nZig Zag Lawncare Okinawa`);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, "_self");
  };

  const sendQuoteEmail = (email, name, quotes) => {
    const latestQuote = quotes?.[0];
    const subject = encodeURIComponent(`Your Quote from Zig Zag Lawncare Okinawa`);
    let quoteDetails = "";
    if (latestQuote) {
      quoteDetails += `Quote ID: ${latestQuote.quote_id}\n`;
      if (latestQuote.quoted_price) quoteDetails += `One-Time Price: $${latestQuote.quoted_price}\n`;
      if (latestQuote.monthly_1x_price) quoteDetails += `Monthly (1x/mo): $${latestQuote.monthly_1x_price}\n`;
      if (latestQuote.monthly_2x_price) quoteDetails += `Monthly (2x/mo): $${latestQuote.monthly_2x_price}\n`;
      if (latestQuote.yearly_1x_price) quoteDetails += `Yearly (1x/mo): $${latestQuote.yearly_1x_price}\n`;
      if (latestQuote.yearly_2x_price) quoteDetails += `Yearly (2x/mo): $${latestQuote.yearly_2x_price}\n`;
      quoteDetails += `Yard Size: ${latestQuote.yard_size}\nGrass Height: ${latestQuote.grass_height}\n`;
    } else {
      quoteDetails = "We're preparing your personalized quote — we'll follow up shortly with pricing details.\n";
    }
    const body = encodeURIComponent(`Hi ${name || "there"},\n\nHere are your quote details from Zig Zag Lawncare Okinawa:\n\n${quoteDetails}\nPlease let us know if you have any questions or would like to proceed.\n\nBest regards,\nZig Zag Lawncare Okinawa`);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, "_self");
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(u => 
        u.name?.toLowerCase().includes(query) ||
        u.email?.toLowerCase().includes(query) ||
        u.address?.toLowerCase().includes(query) ||
        u.user_id?.toLowerCase().includes(query)
      );
    }
    
    if (roleFilter !== "all") {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    
    setFilteredUsers(filtered);
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, { withCredentials: true });
      setUserDetails(response.data);
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      toast.error("Failed to load user details");
    }
  };

  const handleViewUser = async (u) => {
    setSelectedUser(u);
    await fetchUserDetails(u.user_id);
    setShowViewModal(true);
  };

  const handleEditUser = (u) => {
    setSelectedUser(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      phone: u.customer_data?.phone || "",
      role: u.role || "customer",
      address: u.customer_data?.address || ""
    });
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user_id}`, editForm, { withCredentials: true });
      
      // If customer data exists, update it too
      if (selectedUser.customer_data) {
        await axios.put(
          `${API}/admin/customers/${selectedUser.customer_data.customer_id}`,
          { phone: editForm.phone, address: editForm.address, name: editForm.name },
          { withCredentials: true }
        );
      }
      
      toast.success("User updated successfully");
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error("Failed to update user:", error);
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.user_id}`, { withCredentials: true });
      toast.success("User deleted successfully");
      setShowDeleteModal(false);
      fetchUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: "bg-red-500/20 text-red-400 border-red-500/30",
      staff: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      customer: "bg-green-500/20 text-green-400 border-green-500/30"
    };
    return badges[role] || badges.customer;
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin": return <Shield className="w-4 h-4" />;
      case "staff": return <Briefcase className="w-4 h-4" />;
      default: return <UserIcon className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#39FF14] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#121212] border-b border-[#27272A] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-[#A1A1AA] hover:text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-lg" />
            <div>
              <h1 className="font-heading text-xl font-bold text-white uppercase flex items-center gap-2">
                <Users className="w-5 h-5 text-[#39FF14]" />
                User Management
              </h1>
              <p className="text-[#A1A1AA] text-sm">Manage all users and their data</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-white font-medium">{users.length} Total Users</p>
              <p className="text-[#A1A1AA] text-sm">
                {users.filter(u => u.role === "customer").length} Customers • 
                {users.filter(u => u.role === "staff").length} Staff • 
                {users.filter(u => u.role === "admin").length} Admins
              </p>
            </div>
            {newUserCount > 0 && (
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
                  onClick={() => setShowNewUsersAlert(!showNewUsersAlert)}
                  data-testid="new-users-bell"
                >
                  <Bell className="w-4 h-4 mr-1" />
                  {newUserCount} New
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* New User Signup Alert */}
      {showNewUsersAlert && newUserCount > 0 && (
        <div className="mx-6 mt-4 p-4 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-xl" data-testid="new-users-alert">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#39FF14]" />
              <h3 className="text-white font-bold text-sm">{newUserCount} New User{newUserCount > 1 ? "s" : ""} Signed Up</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-[#A1A1AA] hover:text-white"
              onClick={markUsersSeen}
              data-testid="dismiss-new-users"
            >
              Dismiss
            </Button>
          </div>
          <div className="space-y-2">
            {newUsersList.slice(0, 5).map((nu) => (
              <div key={nu.user_id} className="flex items-center justify-between bg-[#0A0A0A] p-3 rounded-lg border border-[#27272A]">
                <div>
                  <p className="text-white text-sm font-medium">{nu.name || nu.email}</p>
                  <p className="text-[#A1A1AA] text-xs">{nu.email} • Joined {nu.created_at ? new Date(nu.created_at).toLocaleDateString() : "recently"}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14] hover:text-black text-xs"
                    onClick={() => openEmailClient(nu.email, nu.name)}
                    data-testid={`welcome-email-${nu.user_id}`}
                  >
                    <Mail className="w-3.5 h-3.5 mr-1" /> Welcome Email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 text-xs"
                    onClick={() => sendQuoteEmail(nu.email, nu.name, null)}
                    data-testid={`send-quote-${nu.user_id}`}
                  >
                    <Send className="w-3.5 h-3.5 mr-1" /> Send Quote
                  </Button>
                </div>
              </div>
            ))}
            {newUserCount > 5 && <p className="text-[#A1A1AA] text-xs text-center">...and {newUserCount - 5} more</p>}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="px-6 py-4 bg-[#121212] border-b border-[#27272A]">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input
              placeholder="Search by name, email, or user ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#0A0A0A] border-[#27272A] text-white"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px] bg-[#0A0A0A] border-[#27272A] text-white">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#27272A]">
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
              <SelectItem value="staff">Staff</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Users Table */}
      <div className="p-6">
        <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-[#27272A] hover:bg-transparent">
                <TableHead className="text-[#A1A1AA]">User</TableHead>
                <TableHead className="text-[#A1A1AA]">Role</TableHead>
                <TableHead className="text-[#A1A1AA]">Contact</TableHead>
                <TableHead className="text-[#A1A1AA]">Address</TableHead>
                <TableHead className="text-[#A1A1AA]">Activity</TableHead>
                {subscriptionsEnabled && <TableHead className="text-[#A1A1AA]">Subscription</TableHead>}
                <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((u) => (
                <TableRow key={u.user_id} className="border-[#27272A] hover:bg-[#1a1a1a]">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10 border border-[#27272A]">
                        <AvatarImage src={u.picture} />
                        <AvatarFallback className="bg-[#39FF14]/20 text-[#39FF14]">
                          {u.name?.charAt(0)?.toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-white font-medium">{u.name || "Unknown"}</p>
                        <p className="text-[#A1A1AA] text-xs">{u.user_id}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadge(u.role)}`}>
                      {getRoleIcon(u.role)}
                      {u.role?.charAt(0).toUpperCase() + u.role?.slice(1)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="text-white text-sm flex items-center gap-1">
                        <Mail className="w-3 h-3 text-[#A1A1AA]" />
                        {u.email}
                      </p>
                      {u.customer_data?.phone && (
                        <p className="text-[#A1A1AA] text-xs flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {u.customer_data.phone}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell data-testid={`user-address-${u.user_id}`}>
                    {u.address ? (
                      <p className="text-white text-sm flex items-start gap-1 max-w-[220px]">
                        <MapPin className="w-3 h-3 mt-1 shrink-0 text-[#39FF14]" />
                        <span className="break-words">{u.address}</span>
                      </p>
                    ) : (
                      <span className="text-red-400/80 text-xs italic">No address yet</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 text-xs">
                      <p className="text-[#A1A1AA]">
                        <FileText className="w-3 h-3 inline mr-1" />
                        {u.quotes_count || 0} quotes
                      </p>
                      <p className="text-[#A1A1AA]">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {u.jobs_count || 0} jobs
                      </p>
                    </div>
                  </TableCell>
                  {subscriptionsEnabled && <TableCell>
                    {u.subscription ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                        u.subscription.status === "active" || u.subscription.is_active
                          ? "bg-green-500/20 text-green-400" 
                          : "bg-yellow-500/20 text-yellow-400"
                      }`}>
                        <CreditCard className="w-3 h-3" />
                        {u.subscription.plan_type || u.subscription.plan} ({u.subscription.status || (u.subscription.is_active ? "active" : "inactive")})
                      </span>
                    ) : (
                      <span className="text-[#A1A1AA] text-xs">No subscription</span>
                    )}
                  </TableCell>}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEmailClient(u.email, u.name)}
                        className="text-[#A1A1AA] hover:text-[#39FF14] hover:bg-[#39FF14]/10"
                        title="Send Email"
                        data-testid={`email-user-${u.user_id}`}
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => sendQuoteEmail(u.email, u.name, u.quotes)}
                        className="text-[#A1A1AA] hover:text-orange-400 hover:bg-orange-500/10"
                        title="Send Quote"
                        data-testid={`quote-user-${u.user_id}`}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewUser(u)}
                        className="text-[#A1A1AA] hover:text-white hover:bg-[#27272A]"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditUser(u)}
                        className="text-[#A1A1AA] hover:text-[#39FF14] hover:bg-[#39FF14]/10"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(u);
                          setShowDeleteModal(true);
                        }}
                        className="text-[#A1A1AA] hover:text-red-400 hover:bg-red-500/10"
                        disabled={u.user_id === user?.user_id}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {filteredUsers.length === 0 && (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-[#27272A] mx-auto mb-4" />
              <p className="text-[#A1A1AA]">No users found</p>
            </div>
          )}
        </div>
      </div>

      {/* View User Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="w-12 h-12 border-2 border-[#39FF14]">
                <AvatarImage src={selectedUser?.picture} />
                <AvatarFallback className="bg-[#39FF14]/20 text-[#39FF14] text-lg">
                  {selectedUser?.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-xl">{selectedUser?.name}</p>
                <p className="text-sm text-[#A1A1AA] font-normal">{selectedUser?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {userDetails && (
            <Tabs defaultValue="info" className="mt-4">
              <TabsList className="bg-[#0A0A0A] border border-[#27272A]">
                <TabsTrigger value="info">User Info</TabsTrigger>
                <TabsTrigger value="quotes">Quotes ({userDetails.quotes?.length || 0})</TabsTrigger>
                <TabsTrigger value="jobs">Jobs ({userDetails.jobs?.length || 0})</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#0A0A0A] rounded-lg p-4">
                    <p className="text-[#A1A1AA] text-sm mb-1">User ID</p>
                    <p className="text-white font-mono text-sm">{userDetails.user?.user_id}</p>
                  </div>
                  <div className="bg-[#0A0A0A] rounded-lg p-4">
                    <p className="text-[#A1A1AA] text-sm mb-1">Role</p>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadge(userDetails.user?.role)}`}>
                      {getRoleIcon(userDetails.user?.role)}
                      {userDetails.user?.role?.charAt(0).toUpperCase() + userDetails.user?.role?.slice(1)}
                    </span>
                  </div>
                  {userDetails.customer && (
                    <>
                      <div className="bg-[#0A0A0A] rounded-lg p-4">
                        <p className="text-[#A1A1AA] text-sm mb-1">Phone</p>
                        <p className="text-white">{userDetails.customer.phone || "Not provided"}</p>
                      </div>
                      <div className="bg-[#0A0A0A] rounded-lg p-4">
                        <p className="text-[#A1A1AA] text-sm mb-1">Address</p>
                        <p className="text-white">{userDetails.customer.address || "Not provided"}</p>
                      </div>
                    </>
                  )}
                </div>
                
                {subscriptionsEnabled && userDetails.subscription && (
                  <div className="bg-[#064E3B] border border-[#39FF14]/30 rounded-lg p-4">
                    <p className="text-[#39FF14] font-medium mb-2">Active Subscription</p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-[#A1A1AA]">Plan</p>
                        <p className="text-white capitalize">{userDetails.subscription.plan_type}</p>
                      </div>
                      <div>
                        <p className="text-[#A1A1AA]">Status</p>
                        <p className="text-white capitalize">{userDetails.subscription.status}</p>
                      </div>
                      <div>
                        <p className="text-[#A1A1AA]">Discount</p>
                        <p className="text-white">{userDetails.subscription.discount_percent}%</p>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="quotes" className="mt-4">
                {userDetails.quotes?.length > 0 ? (
                  <div className="space-y-3">
                    {userDetails.quotes.map((quote) => (
                      <div key={quote.quote_id} className="bg-[#0A0A0A] rounded-lg p-4 border border-[#27272A]">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-mono text-xs text-[#A1A1AA]">{quote.quote_id}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            quote.status === "sent" ? "bg-blue-500/20 text-blue-400" :
                            quote.status === "accepted" ? "bg-green-500/20 text-green-400" :
                            "bg-yellow-500/20 text-yellow-400"
                          }`}>
                            {quote.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-[#A1A1AA]">Yard Size</p>
                            <p className="text-white capitalize">{quote.yard_size}</p>
                          </div>
                          <div>
                            <p className="text-[#A1A1AA]">Grass Height</p>
                            <p className="text-white capitalize">{quote.grass_height}</p>
                          </div>
                          <div>
                            <p className="text-[#A1A1AA]">Quoted Price</p>
                            <p className="text-[#39FF14] font-medium">
                              {quote.quoted_price ? `$${quote.quoted_price}` : "Pending"}
                            </p>
                          </div>
                        </div>
                        {quote.customer_address && (
                          <p className="text-[#A1A1AA] text-sm mt-2 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {quote.customer_address}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#A1A1AA] text-center py-8">No quotes found</p>
                )}
              </TabsContent>
              
              <TabsContent value="jobs" className="mt-4">
                {userDetails.jobs?.length > 0 ? (
                  <div className="space-y-3">
                    {userDetails.jobs.map((job) => (
                      <div key={job.job_id} className="bg-[#0A0A0A] rounded-lg p-4 border border-[#27272A]">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-mono text-xs text-[#A1A1AA]">{job.job_id}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            job.status === "completed" ? "bg-green-500/20 text-green-400" :
                            job.status === "scheduled" ? "bg-blue-500/20 text-blue-400" :
                            "bg-yellow-500/20 text-yellow-400"
                          }`}>
                            {job.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-[#A1A1AA]">Scheduled Date</p>
                            <p className="text-white">{job.scheduled_date}</p>
                          </div>
                          <div>
                            <p className="text-[#A1A1AA]">Price</p>
                            <p className="text-[#39FF14] font-medium">${job.price}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#A1A1AA] text-center py-8">No jobs found</p>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#39FF14]" />
              Edit User
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Update user information. Changes will be reflected across all related records.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#27272A]">
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-[#27272A]">
              Cancel
            </Button>
            <Button onClick={handleSaveUser} disabled={saving} className="bg-[#39FF14] text-black hover:bg-[#32D711]">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Delete User
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Are you sure you want to delete <span className="text-white font-medium">{selectedUser?.name}</span>? 
              This will also remove their customer profile and subscription. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="border-[#27272A]">
              Cancel
            </Button>
            <Button onClick={handleDeleteUser} disabled={saving} className="bg-red-500 text-white hover:bg-red-600">
              {saving ? "Deleting..." : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
